import { useState, useEffect, useRef } from 'react'
import { Search, ArrowRight, AlertTriangle, CheckCircle, Loader2, X, GitMerge, Users, ShoppingCart, Truck, Package, FileText, Building, CreditCard, Percent } from 'lucide-react'
import clientesService from '../../services/clientesService'
import { useDebounce } from '../../hooks/useDebounce'

const ETAPAS = { SELECIONAR: 1, PREVIEW: 2, CONFIRMAR: 3 }

const LABELS_TRANSFERENCIA = [
  { key: 'lotes_como_vendedor',  label: 'Lotes como vendedor',   icon: Package },
  { key: 'lotes_como_comprador', label: 'Lotes como comprador',  icon: ShoppingCart },
  { key: 'fazendas',             label: 'Fazendas',              icon: Building },
  { key: 'dados_bancarios',      label: 'Dados bancários',       icon: CreditCard },
  { key: 'sociedades',           label: 'Sociedades',            icon: Percent },
  { key: 'contas_receber',       label: 'Contas a receber',      icon: FileText },
  { key: 'recibos',              label: 'Recibos',               icon: FileText },
  { key: 'fretes',               label: 'Fretes',                icon: Truck },
  { key: 'entradas_animais',     label: 'Entradas de animais',   icon: Users },
  { key: 'saidas_animais',       label: 'Saídas de animais',     icon: Users },
]

export default function MesclarClienteModal({ isOpen, onClose, cliente, organizadorId, onSuccess }) {
  const [etapa, setEtapa] = useState(ETAPAS.SELECIONAR)
  const [busca, setBusca] = useState('')
  const debouncedBusca = useDebounce(busca, 350)
  const [resultados, setResultados] = useState([])
  const [buscando, setBuscando] = useState(false)
  const [destino, setDestino] = useState(null)
  const [preview, setPreview] = useState(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [confirmacao, setConfirmacao] = useState('')
  const [mesclando, setMesclando] = useState(false)
  const [erro, setErro] = useState('')
  const inputRef = useRef(null)

  const totalTransferencias = preview
    ? Object.values(preview.transferencias).reduce((s, v) => s + v, 0)
    : 0

  useEffect(() => {
    if (!isOpen) return
    setEtapa(ETAPAS.SELECIONAR)
    setBusca('')
    setResultados([])
    setDestino(null)
    setPreview(null)
    setConfirmacao('')
    setErro('')
    setTimeout(() => inputRef.current?.focus(), 100)
  }, [isOpen])

  useEffect(() => {
    if (debouncedBusca.length < 2) { setResultados([]); return }
    setBuscando(true)
    clientesService.searchByName(organizadorId, debouncedBusca)
      .then(data => setResultados(data.filter(c => c.id !== cliente?.id)))
      .catch(() => setResultados([]))
      .finally(() => setBuscando(false))
  }, [debouncedBusca, organizadorId, cliente?.id])

  const handleSelecionarDestino = async (clienteDestino) => {
    setDestino(clienteDestino)
    setEtapa(ETAPAS.PREVIEW)
    setLoadingPreview(true)
    setErro('')
    try {
      const data = await clientesService.previewMesclar(organizadorId, cliente.id, clienteDestino.id)
      setPreview(data)
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao carregar prévia')
      setEtapa(ETAPAS.SELECIONAR)
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleMesclar = async () => {
    setMesclando(true)
    setErro('')
    try {
      await clientesService.mesclar(organizadorId, cliente.id, destino.id)
      onSuccess(destino.id)
      onClose()
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao mesclar clientes')
      setMesclando(false)
    }
  }

  const confirmacaoCorreta = confirmacao.trim().toUpperCase() === 'MESCLAR'

  if (!isOpen || !cliente) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-600 px-6 py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <GitMerge className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Mesclar Cliente Duplicado</h2>
              <p className="text-orange-100 text-sm">Operação irreversível — apenas administradores</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/20 transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Indicador de etapas */}
        <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2 flex-shrink-0">
          {[
            { n: 1, label: 'Selecionar destino' },
            { n: 2, label: 'Conferir dados' },
            { n: 3, label: 'Confirmar' },
          ].map(({ n, label }, idx) => (
            <div key={n} className="flex items-center gap-2">
              {idx > 0 && <div className="w-8 h-px bg-gray-300" />}
              <div className="flex items-center gap-1.5">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  etapa > n ? 'bg-green-500 text-white' :
                  etapa === n ? 'bg-orange-500 text-white' :
                  'bg-gray-200 text-gray-500'
                }`}>
                  {etapa > n ? <CheckCircle className="w-3.5 h-3.5" /> : n}
                </div>
                <span className={`text-xs font-medium hidden sm:inline ${etapa === n ? 'text-orange-600' : 'text-gray-400'}`}>
                  {label}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Conteúdo scrollável */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── ETAPA 1: Selecionar destino ── */}
          {etapa === ETAPAS.SELECIONAR && (
            <div className="space-y-4">
              {/* Cliente origem */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-1">Excluir este cadastro (origem)</p>
                <p className="text-base font-bold text-gray-900">{cliente.nome}</p>
                {cliente.cpf_cnpj && <p className="text-sm text-gray-500">{cliente.cpf_cnpj}</p>}
              </div>

              <div className="flex items-center gap-3 text-gray-400">
                <div className="flex-1 h-px bg-gray-200" />
                <ArrowRight className="w-4 h-4 flex-shrink-0" />
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Busca do destino */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Manter este cadastro (destino)</p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  {buscando && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />}
                  <input
                    ref={inputRef}
                    type="text"
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    placeholder="Buscar cliente por nome..."
                    className="w-full pl-9 pr-10 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                  />
                </div>

                {/* Resultados */}
                {resultados.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100 shadow-sm">
                    {resultados.map(c => (
                      <button
                        key={c.id}
                        onClick={() => handleSelecionarDestino(c)}
                        className="w-full text-left px-4 py-3 hover:bg-orange-50 transition-colors flex items-center justify-between group"
                      >
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{c.nome}</p>
                          <p className="text-xs text-gray-400">ID #{c.id}{c.cpf_cnpj ? ` · ${c.cpf_cnpj}` : ''}{c.cidade ? ` · ${c.cidade}` : ''}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-orange-500 transition-colors" />
                      </button>
                    ))}
                  </div>
                )}

                {debouncedBusca.length >= 2 && resultados.length === 0 && !buscando && (
                  <p className="mt-2 text-sm text-gray-500 text-center py-3">Nenhum cliente encontrado</p>
                )}
              </div>

              {erro && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{erro}</div>
              )}
            </div>
          )}

          {/* ── ETAPA 2: Preview ── */}
          {etapa === ETAPAS.PREVIEW && (
            <div className="space-y-5">
              {loadingPreview ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                  <p className="text-sm text-gray-500">Carregando prévia...</p>
                </div>
              ) : preview && (
                <>
                  {/* Aviso */}
                  <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Atenção: esta operação não tem reversão</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Todo o histórico será transferido e <strong>{preview.origem.nome}</strong> será excluído permanentemente.
                      </p>
                    </div>
                  </div>

                  {/* Seta origem → destino */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                      <p className="text-xs text-red-500 font-semibold uppercase tracking-wider mb-0.5">Será excluído</p>
                      <p className="font-bold text-gray-900 text-sm">{preview.origem.nome}</p>
                      <p className="text-xs text-gray-400">ID #{preview.origem.id}</p>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <ArrowRight className="w-5 h-5 text-orange-500" />
                      <span className="text-xs text-gray-400">merge</span>
                    </div>
                    <div className="flex-1 bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                      <p className="text-xs text-green-600 font-semibold uppercase tracking-wider mb-0.5">Será mantido</p>
                      <p className="font-bold text-gray-900 text-sm">{preview.destino.nome}</p>
                      <p className="text-xs text-gray-400">ID #{preview.destino.id}</p>
                    </div>
                  </div>

                  {/* Tabela de transferências */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      O que será transferido ({totalTransferencias} registro{totalTransferencias !== 1 ? 's' : ''} no total):
                    </p>
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      {LABELS_TRANSFERENCIA.map(({ key, label, icon: Icon }, idx) => {
                        const qtd = preview.transferencias[key] || 0
                        return (
                          <div key={key} className={`flex items-center justify-between px-4 py-3 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Icon className="w-4 h-4 text-gray-400" />
                              {label}
                            </div>
                            <span className={`text-sm font-semibold ${qtd > 0 ? 'text-orange-600' : 'text-gray-300'}`}>
                              {qtd}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── ETAPA 3: Confirmar ── */}
          {etapa === ETAPAS.CONFIRMAR && (
            <div className="space-y-5">
              <div className="bg-red-50 border border-red-300 rounded-xl p-4 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-red-800">Confirmação final obrigatória</p>
                  <p className="text-xs text-red-700 mt-1">
                    Você está prestes a excluir <strong>{preview?.origem.nome}</strong> e transferir
                    todos os {totalTransferencias} registros para <strong>{preview?.destino.nome}</strong>.
                    Esta ação <strong>não pode ser desfeita</strong>.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Digite <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-red-600">MESCLAR</span> para confirmar
                </label>
                <input
                  type="text"
                  value={confirmacao}
                  onChange={e => setConfirmacao(e.target.value)}
                  placeholder="Digite MESCLAR"
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 text-sm font-mono transition-colors ${
                    confirmacaoCorreta
                      ? 'border-green-400 focus:ring-green-300 bg-green-50'
                      : 'border-gray-300 focus:ring-orange-300'
                  }`}
                  autoFocus
                />
                {confirmacaoCorreta && (
                  <p className="mt-1.5 text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> Confirmação válida
                  </p>
                )}
              </div>

              {erro && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{erro}</div>
              )}
            </div>
          )}
        </div>

        {/* Footer com botões */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3 flex-shrink-0">
          <button
            onClick={() => {
              if (etapa === ETAPAS.SELECIONAR) onClose()
              else setEtapa(etapa - 1)
            }}
            disabled={mesclando}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors disabled:opacity-50"
          >
            {etapa === ETAPAS.SELECIONAR ? 'Cancelar' : '← Voltar'}
          </button>

          {etapa === ETAPAS.PREVIEW && !loadingPreview && preview && (
            <button
              onClick={() => setEtapa(ETAPAS.CONFIRMAR)}
              className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors flex items-center gap-2"
            >
              Continuar para confirmação
              <ArrowRight className="w-4 h-4" />
            </button>
          )}

          {etapa === ETAPAS.CONFIRMAR && (
            <button
              onClick={handleMesclar}
              disabled={!confirmacaoCorreta || mesclando}
              className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl transition-colors flex items-center gap-2"
            >
              {mesclando ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Mesclando...</>
              ) : (
                <><GitMerge className="w-4 h-4" /> Mesclar e excluir duplicado</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
